import { Axis } from 'd3-axis';
import { ScaleLinear } from 'd3-scale';
import { minBy } from 'lodash';
import { CSSProperties, Component, SVGTextElementAttributes } from 'react';
import styled from 'styled-components';
import { getTextStyle } from '../content/texts/bou-text/text-style';
import { BouColor } from '../global/bou-colors';

interface Props {
  axis: Axis<number>;
  axisType: 'left' | 'bottom';
  className?: string;
  transform?: string;
  selection?: number;
  selectionTickFormat?: (value: number) => string;
  tickFormatter?: (value: number) => string;
}

interface DefaultProps {
  selectionTickFormat: (value: number) => string;
}

type PropsWithDefault = Props & DefaultProps;

const TickText = styled.text`
  ${getTextStyle({ size: 'xxxxs' })};
  fill: ${BouColor.BLACK};
`;

const MainLine = styled.line`
  stroke: #999;
  shape-rendering: crispEdges;
`;

const HideableTick = styled.g`
  transition: opacity 0.1s ease-in-out;
`;

export default class ReactAxis extends Component<Props> {
  public static defaultProps: DefaultProps = {
    selectionTickFormat: (value: number) => value.toFixed(1),
  };

  private tickValues(): number[] {
    const { axis } = this.props;
    const axisTickValues = axis.tickValues();
    if (axisTickValues) {
      return axisTickValues;
    }
    const scale = axis.scale();
    return 'ticks' in scale ? (scale as { ticks(): number[] }).ticks() : [];
  }

  private scale(): ScaleLinear<number, number> {
    return this.props.axis.scale();
  }

  private renderTick(value: number, renderedValue: string | JSX.Element | null, textStyle: CSSProperties = {}) {
    const { axisType, axis } = this.props;
    let translateX = 0;
    let translateY = 0;
    let textY = 0;
    let textX = 0;
    let textDy = '0.35em';
    let textAnchor: SVGTextElementAttributes<SVGTextElement>['textAnchor'] = 'middle';

    if (axisType === 'left') {
      translateY = this.scale()(value) ?? 0;
      textX = -axis.tickSize() - axis.tickPadding();
      textAnchor = 'end' as const;
    }
    if (axisType === 'bottom') {
      translateX = this.scale()(value) ?? 0;
      textY = axis.tickSize() + axis.tickPadding();
      textDy = '0.71em';
    }
    return (
      <g transform={`translate(${translateX}, ${translateY})`}>
        <TickText y={textY} x={textX} dy={textDy} textAnchor={textAnchor} style={textStyle}>
          {renderedValue}
        </TickText>
      </g>
    );
  }

  private getTickFormat() {
    if (this.props.tickFormatter) {
      return this.props.tickFormatter;
    }
    const scale = this.props.axis.scale();
    const tickFormat =
      'tickFormat' in scale ? (scale as { tickFormat(): (d: number) => string }).tickFormat() : undefined;
    const format = tickFormat !== undefined ? tickFormat : (value: number) => value.toFixed(2);
    return format;
  }

  private drawTicks() {
    const { selection } = this.props;
    const format = this.getTickFormat();
    const tickValues = this.tickValues();
    const closestValue = selection && minBy(tickValues, value => Math.abs(value - selection));
    return tickValues.map((value, index) => (
      <HideableTick
        key={index}
        style={{
          opacity: closestValue === value ? 0 : 1.0,
        }}
      >
        {this.renderTick(value, format(value))}
      </HideableTick>
    ));
  }

  private drawSelection() {
    const { selection, selectionTickFormat } = this.props as PropsWithDefault;
    if (!selection) {
      return;
    }
    return this.renderTick(selection, selectionTickFormat(selection), {
      fill: 'black',
    });
  }

  private mainLine() {
    const { axisType } = this.props;

    let x1 = 0;
    let x2 = 0;
    let y1 = 0;
    let y2 = 0;

    const [min, max] = this.scale().domain();

    if (axisType === 'left') {
      y1 = this.scale()(min) ?? 0;
      y2 = this.scale()(max) ?? 0;
    }

    if (axisType === 'bottom') {
      x1 = this.scale()(min) ?? 0;
      x2 = this.scale()(max) ?? 0;
    }

    return <MainLine x1={x1} x2={x2} y1={y1} y2={y2} />;
  }

  public render() {
    const { className, transform } = this.props;
    return (
      <g transform={transform}>
        <g className={className} />
        {this.mainLine()}
        {this.drawTicks()}
        {this.drawSelection()}
      </g>
    );
  }
}
